import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { IMAGES } from "@/lib/menuData";
import { PartyPopper, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full border-2 border-[color:var(--brand-ink)] bg-white px-4 py-3 font-medium placeholder:text-[color:var(--brand-ink-2)] focus:bg-[color:var(--brand-bg-2)] focus:outline-none rounded-none";

export const CateringForm = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    event_date: "",
    guests: "",
    event_type: "Catering",
    details: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share at least your name and phone.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        guests: form.guests ? parseInt(form.guests, 10) : null,
      };
      await axios.post(`${API}/catering`, payload);
      toast.success("¡Gracias! We got your request and will call you back soon.");
      setForm({
        name: "",
        phone: "",
        email: "",
        event_date: "",
        guests: "",
        event_type: "Catering",
        details: "",
      });
    } catch (err) {
      toast.error("Couldn't send right now. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="catering" data-testid="catering-section" className="border-b-2 border-[color:var(--brand-ink)] bg-[color:var(--brand-ink)] text-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="grid lg:grid-cols-12 gap-8 items-stretch">
          <div className="lg:col-span-5 relative">
            <div className="border-2 border-white shadow-brutal overflow-hidden h-full min-h-[360px]">
              <img src={IMAGES.catering} alt="Catering spread" className="w-full h-full object-cover" />
            </div>
            <span className="absolute -top-3 -left-3 bg-[color:var(--brand-yellow)] text-[color:var(--brand-ink)] font-display uppercase px-3 py-1 border-2 border-[color:var(--brand-ink)] shadow-brutal-sm stamp">
              Catering
            </span>
          </div>

          <div className="lg:col-span-7">
            <div className="flex items-center gap-3">
              <PartyPopper size={22} strokeWidth={2.75} className="text-[color:var(--brand-yellow)]" />
              <p className="font-bold text-sm uppercase tracking-[0.2em] text-[color:var(--brand-yellow)]">Catering & Private Events</p>
            </div>
            <h2 className="font-display text-5xl sm:text-6xl uppercase mt-2">
              Feed the <span className="text-[color:var(--brand-red)]">Party</span>.
            </h2>
            <p className="mt-4 text-white/80 max-w-xl">
              Weddings, birthdays, company lunches, quinceañeras — let us bring the feast. Send us the details and we'll call back to build your menu.
            </p>

            <form onSubmit={onSubmit} data-testid="catering-form" className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 bg-white text-[color:var(--brand-ink)] p-6 border-2 border-white shadow-brutal">
              <input
                required
                name="name"
                value={form.name}
                onChange={onChange}
                placeholder="Your Name *"
                data-testid="catering-name"
                className={inputClass}
              />
              <input
                required
                name="phone"
                value={form.phone}
                onChange={onChange}
                placeholder="Phone Number *"
                data-testid="catering-phone"
                className={inputClass}
              />
              <input
                name="email"
                value={form.email}
                onChange={onChange}
                placeholder="Email (optional)"
                data-testid="catering-email"
                className={`${inputClass} sm:col-span-2`}
              />
              <input
                type="date"
                name="event_date"
                value={form.event_date}
                onChange={onChange}
                data-testid="catering-date"
                className={inputClass}
              />
              <input
                type="number"
                min="1"
                name="guests"
                value={form.guests}
                onChange={onChange}
                placeholder="Guests"
                data-testid="catering-guests"
                className={inputClass}
              />
              <select
                name="event_type"
                value={form.event_type}
                onChange={onChange}
                data-testid="catering-type"
                className={`${inputClass} sm:col-span-2`}
              >
                <option>Catering</option>
                <option>Private Event</option>
                <option>Food Truck Booking</option>
                <option>Other</option>
              </select>
              <textarea
                name="details"
                value={form.details}
                onChange={onChange}
                placeholder="Tell us about your event, favorite dishes, dietary notes..."
                rows={4}
                data-testid="catering-details"
                className={`${inputClass} sm:col-span-2`}
              />
              <button
                type="submit"
                disabled={submitting}
                data-testid="catering-submit"
                className="btn-brutal sm:col-span-2 inline-flex items-center justify-center gap-2 bg-[color:var(--brand-red)] text-white font-bold uppercase tracking-wide px-5 py-4 border-2 border-[color:var(--brand-ink)] shadow-brutal disabled:opacity-60"
              >
                <Send size={16} strokeWidth={2.75} />
                {submitting ? "Sending..." : "Request Catering"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CateringForm;
